import { EventSourceController } from "event-source-plus";
import { ChatApi } from "../api/chatApi";

export interface SseMessageHandler {
  onMessage: (msg: { data: string }) => void;
  onAbort?: (wasManualAbort: boolean) => void;
}

interface Connection {
  id: string;
  role: "primary" | "secondary";
  controller: EventSourceController;
}

export class SseConnectionManager {
  private static readonly REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes
  private static readonly SWITCH_CONNECTION_TIMEOUT = 10 * 60 * 1000; // 10 minutes
  private static readonly PROCESSED_EVENT_IDS_HIGH_WATER_MARK = 10_000;
  private static readonly PROCESSED_EVENT_IDS_LOW_WATER_MARK = 5_000;

  private projectId: string;
  private messageHandler: SseMessageHandler;
  private primaryConnection?: Connection;
  private secondaryConnection?: Connection;
  private lastRefreshTime: number = 0;
  private processedEventIds = new Set<string>();
  private pendingConnectionSwitch = false;
  private connectionCounter = 0;
  private visibilityHandler?: (event: Event) => void;
  private refreshTimer?: NodeJS.Timeout;
  private switchTimeout?: NodeJS.Timeout;
  private isDisposed = false;
  private manualAbortIds = new Set<string>();

  constructor(projectId: string, messageHandler: SseMessageHandler) {
    this.projectId = projectId;
    this.messageHandler = messageHandler;
  }

  async start() {
    await this.establishPrimaryConnection();
    this.lastRefreshTime = Date.now();
    this.setupVisibilityHandler();
    this.scheduleRefresh();
  }

  private setupVisibilityHandler() {
    this.visibilityHandler = async () => {
      if (document.visibilityState === "visible" && !this.isDisposed) {
        const timeSinceLastRefresh = Date.now() - this.lastRefreshTime;
        if (timeSinceLastRefresh >= SseConnectionManager.REFRESH_INTERVAL) {
          await this.refreshConnections();
        } else if (
          !this.primaryConnection ||
          this.primaryConnection.controller.signal.aborted
        ) {
          await this.refreshConnections();
        } else {
          this.scheduleRefresh();
        }
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  private generateConnectionId(): string {
    return `conn_${++this.connectionCounter}`;
  }

  private async establishConnection(): Promise<
    [string, EventSourceController]
  > {
    const connId = this.generateConnectionId();
    const eventSource = await ChatApi.getSSE(this.projectId);
    let errorCount = 0;
    const controller = eventSource.listen({
      onMessage: (msg) => this.handleMessage(msg, connId),
      onRequestError: () => {
        errorCount++;
        if (errorCount >= 4) {
          controller.abort();
          this.primaryConnection = undefined;
        }
      },
      onResponseError: () => {
        errorCount++;
        if (errorCount >= 4) {
          controller.abort();
          this.primaryConnection = undefined;
        }
      },
    });

    // Add abort signal handler
    controller.signal.onabort = () => {
      const wasManualAbort = this.manualAbortIds.has(connId);
      if (wasManualAbort) {
        this.manualAbortIds.delete(connId);
      }

      if (this.primaryConnection?.id === connId) {
        this.primaryConnection = undefined;
      } else if (this.secondaryConnection?.id === connId) {
        this.secondaryConnection = undefined;
      }
      this.messageHandler.onAbort?.(wasManualAbort);
    };
    return [connId, controller];
  }

  private async establishPrimaryConnection() {
    if (this.isDisposed) return;

    if (this.primaryConnection) {
      this.manualAbortIds.add(this.primaryConnection.id);
      this.primaryConnection.controller.abort();
    }

    const [connId, controller] = await this.establishConnection();

    this.primaryConnection = {
      id: connId,
      role: "primary",
      controller: controller,
    };
  }

  private async establishSecondaryConnection() {
    if (this.isDisposed) return;

    if (this.secondaryConnection) {
      this.manualAbortIds.add(this.secondaryConnection.id);
      this.secondaryConnection.controller.abort();
    }

    const [connId, controller] = await this.establishConnection();

    this.secondaryConnection = {
      id: connId,
      role: "secondary",
      controller,
    };
  }

  private handleMessage(msg: { data: string }, connectionId: string) {
    if (this.isDisposed) {
      return;
    }

    const connection =
      this.primaryConnection?.id === connectionId
        ? this.primaryConnection
        : this.secondaryConnection;
    if (!connection) {
      return;
    }

    const data = JSON.parse(msg.data);
    const eventId = data.body.eventId ?? "";

    // If we've already processed this message, skip it
    if (eventId) {
      if (this.shouldProcessEvent(eventId)) {
        this.markEventAsProcessed(eventId);
      } else {
        return;
      }
    }

    // Deliver the message first
    this.messageHandler.onMessage(msg);

    // If this is the first message on the secondary connection during a refresh,
    // switch the connections after the current message handler completes
    // This prevents race conditions with rapidly arriving messages
    if (connection.role === "secondary" && this.pendingConnectionSwitch) {
      setTimeout(() => this.switchConnections(), 0);
    }
  }

  private shouldProcessEvent(eventId: string): boolean {
    return !this.processedEventIds.has(eventId);
  }

  private markEventAsProcessed(eventId: string) {
    this.processedEventIds.add(eventId);
    // If processedEventIds exceeds the limit, trim it down to 50% capacity
    // This batch removal is more efficient than removing one element at a time
    if (
      this.processedEventIds.size >
      SseConnectionManager.PROCESSED_EVENT_IDS_HIGH_WATER_MARK
    ) {
      const itemsToRemove =
        this.processedEventIds.size -
        SseConnectionManager.PROCESSED_EVENT_IDS_LOW_WATER_MARK;

      // Drop the oldest events
      const allIds = Array.from(this.processedEventIds);
      this.processedEventIds = new Set(allIds.slice(-itemsToRemove));
    }
  }

  private async refreshConnections() {
    if (this.isDisposed) return;

    this.lastRefreshTime = Date.now();

    if (
      !this.primaryConnection ||
      this.primaryConnection.controller.signal.aborted
    ) {
      await this.establishPrimaryConnection();
    } else {
      this.pendingConnectionSwitch = true;
      await this.establishSecondaryConnection();

      // Force switch after 10 minutes if it hasn't happened naturally
      if (this.switchTimeout) {
        clearTimeout(this.switchTimeout);
      }
      this.switchTimeout = setTimeout(() => {
        if (this.pendingConnectionSwitch && this.secondaryConnection) {
          this.switchConnections();
        }
      }, SseConnectionManager.SWITCH_CONNECTION_TIMEOUT);
    }

    this.scheduleRefresh();
  }

  private scheduleRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = setTimeout(() => {
      if (document.visibilityState === "visible" && !this.isDisposed) {
        this.refreshConnections();
      }
    }, SseConnectionManager.REFRESH_INTERVAL);
  }

  private switchConnections() {
    // Clear the timeout when switch happens
    if (this.switchTimeout) {
      clearTimeout(this.switchTimeout);
      this.switchTimeout = undefined;
    }

    if (
      !this.secondaryConnection ||
      this.secondaryConnection.controller.signal.aborted
    ) {
      // Clean up aborted secondary connection if it exists
      if (this.secondaryConnection?.controller.signal.aborted) {
        this.secondaryConnection = undefined;
      }
      return;
    }

    const oldPrimaryConnection = this.primaryConnection;

    // Update roles
    this.secondaryConnection.role = "primary";
    this.primaryConnection = this.secondaryConnection;
    this.secondaryConnection = undefined;

    // Close old primary connection
    if (oldPrimaryConnection) {
      this.manualAbortIds.add(oldPrimaryConnection.id);
      oldPrimaryConnection.controller.abort();
    }

    this.pendingConnectionSwitch = false;
  }

  dispose() {
    if (this.isDisposed) return;

    this.isDisposed = true;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }

    if (this.switchTimeout) {
      clearTimeout(this.switchTimeout);
      this.switchTimeout = undefined;
    }

    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = undefined;
    }

    if (this.primaryConnection) {
      this.primaryConnection.controller.abort();
      this.primaryConnection = undefined;
    }

    if (this.secondaryConnection) {
      this.secondaryConnection.controller.abort();
      this.secondaryConnection = undefined;
    }

    this.processedEventIds.clear();
    this.pendingConnectionSwitch = false;
    this.manualAbortIds.clear();
  }
}
